/**
 * The three dots that mean "Alfie is answering, the first words have not arrived yet".
 *
 * WHY IT IS SHARED. This existed twice and had already drifted: `WidgetChatView`'s `TypingDots`
 * used 7px dots rising 0.35 → 1 in opacity, `AlfieChatView` used 8px dots falling 0.70 → 0.25.
 * Same idea, two looks, in one product. The Xero account chat needed it too, and a third divergent
 * copy is how a pattern stops being a pattern — so the widget's shape wins (it was the tidier one)
 * and all three now render this.
 *
 * BUBBLE-LESS ON PURPOSE, a rule inherited from `AlfieChatView`: the first streamed chunk replaces
 * the dots in place rather than popping out of a card, so the answer appears to arrive where the
 * waiting was, not somewhere new.
 *
 * `aria-hidden`, deliberately. Every surface that shows this already sits inside a live region
 * (`role="log"`), and the empty assistant message that triggers it is itself announced. Giving the
 * dots their own `role="status"` makes a screen reader say it twice.
 */

/** Staggered so the three read as one travelling wave rather than three separate blinks. */
const DOTS: Array<{ delayMs: number; opacity: number }> = [
  { delayMs: 0, opacity: 0.35 },
  { delayMs: 150, opacity: 0.65 },
  { delayMs: 300, opacity: 1 },
];

export function AlfieTypingDots({ className = '' }: { className?: string }) {
  return (
    // A SPAN, not a div: this renders inside a <p> in the Xero account chat, and a div there is
    // invalid nesting that browsers fix by closing the <p> early — which breaks the message layout
    // rather than erroring. `inline-flex` keeps the row behaviour a div gave it.
    <span className={`inline-flex h-[22px] items-center gap-[5px] align-middle ${className}`} aria-hidden>
      {DOTS.map(({ delayMs, opacity }) => (
        <span
          key={delayMs}
          // `motion-reduce:animate-none` is the half the two originals both missed: a bouncing
          // dot is exactly the repeating motion that rule exists for. Stopped, the three still
          // read as a waiting state because their opacities differ.
          className="h-[7px] w-[7px] animate-bounce rounded-full bg-primary motion-reduce:animate-none"
          style={{ animationDelay: `${delayMs}ms`, opacity }}
        />
      ))}
    </span>
  );
}
